import { useEffect } from "react";
import { Navigate } from "react-router-dom";

import { gateFor, type Gate } from "../session/access";
import { useSession } from "../session/SessionProvider";
import { Message } from "../screens/Message";
import { Shell } from "./Shell";
import { applyChrome, chromeFor, type ThemePreference } from "../system/chrome";
import type { Surface } from "../system/navigation";
import { LocaleProvider, preSessionLocale } from "../i18n/LocaleProvider";
import { translate, type Locale } from "../i18n/translate";

// ---------------------------------------------------------------------------
// The protected route.
//
// All it does is render what `gateFor` decided. Every rule lives in
// session/access.ts, which is pure and tested; this file is the wiring, and
// keeping it this thin is what makes the rules testable at all — a decision
// taken inside a component is a decision only a browser can check.
//
// EACH OUTCOME GETS ITS OWN SCREEN. `unavailable` in particular is NOT a
// redirect to sign-in: a dropped connection that logs somebody out teaches them
// to retype a password whenever the page misbehaves, which is a phishing lesson
// delivered by their own employer.
// ---------------------------------------------------------------------------

/**
 * Put the surface's palette and density on <html> for as long as this route is
 * mounted, and take them off again on the way out.
 *
 * IT IS DELIBERATELY OUTSIDE THE GATE SWITCH BELOW, and this is the decision
 * worth stating. The chrome follows the ROUTE, not the outcome: "we cannot
 * reach Verbunk" shown at /console is a console screen and must look like one.
 * Binding it to the `allow` case instead would render every loading, refused
 * and disconnected state in the warm marketing palette — the three states an
 * operator sees on their worst day, each in a skin they have never seen before,
 * which is precisely when a familiar surface is worth most.
 *
 * A hook cannot be called conditionally, so putting it here is also the only
 * arrangement React permits.
 */
function useSurfaceChrome(surface: Surface) {
  // V16 replaces this with the worker's stored choice. Reading the device is
  // §8's own default and costs nothing until then.
  const preference: ThemePreference = "system";
  useEffect(() => {
    const deviceIsDark =
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
        : false;
    return applyChrome(document.documentElement, chromeFor(surface, preference, deviceIsDark));
  }, [surface, preference]);
}

export function Protected({ surface, children }: { surface: Surface; children: React.ReactNode }) {
  const { state, me } = useSession();
  const gate = gateFor(state, me, surface);
  useSurfaceChrome(surface);

  // S63. The worker app's screens before the shell are worded before anybody is
  // known: the person's stored choice if /v1/me has already said it, else the
  // browser. The console and portal stay English, as their shells do.
  const locale = surface === "worker" ? preSessionLocale(me?.authenticated ? me.locale : null) : "en";
  if (gate.kind === "allow") return <Shell surface={surface}>{children}</Shell>;
  return <LocaleProvider locale={locale}>{gateScreen(gate, locale)}</LocaleProvider>;
}

function gateScreen(gate: Exclude<Gate, { kind: "allow" }>, locale: Locale) {
  switch (gate.kind) {
    case "loading":
      // No spinner, no skeleton, no layout that will move. A cold load resolves
      // in a few hundred milliseconds and anything that animates in that window
      // is noise the eye has to discard.
      return <Message tone="neutral" status={translate(locale, "Working")} title={translate(locale, "Loading…")} />;

    case "unavailable":
      return (
        <Message tone="attention" status={translate(locale, "No connection")} title={translate(locale, "We cannot reach Verbunk")}>
          <p>
            {translate(locale, "This is a problem at our end or with your connection — you have not been signed out. Refresh in a moment.")}
          </p>
        </Message>
      );

    case "sign-in":
      return <Navigate to="/sign-in" replace />;

    case "choose-org":
      return <Navigate to="/choose-organisation" replace />;

    case "no-access":
      return (
        <Message tone="attention" status={translate(locale, "No organisation")} title={translate(locale, "Nothing to show yet")}>
          <p>
            {translate(locale, "Your account is not attached to an organisation, or the access it had has been removed. Whoever invited you can put that right.")}
          </p>
        </Message>
      );

    case "redirect":
      return <Navigate to={gate.to} replace />;
  }
}
